import type { CSSProperties } from "react";
import type { SiteConfig } from "@/types/sites";
import { getPaletteClass } from "@/utils/paletteUtils";

// Define the alternate background cycle
type AlternateBackgroundType = "primary" | "soft" | "lighter" | "pattern";

// Background types to cycle through for alternating sections
const BACKGROUND_CYCLE: AlternateBackgroundType[] = [
  "primary",
  "soft",
  "primary",
  "lighter",
];

// Get background type based on section index for consistent alternation
const getBackgroundTypeByIndex = (
  sectionIndex: number
): AlternateBackgroundType => {
  return BACKGROUND_CYCLE[sectionIndex % BACKGROUND_CYCLE.length];
};


/**
 * Gets the appropriate background class for a specific section with alternate backgrounds support.
 * When alternate backgrounds are enabled, cycles through different background types based on section index.
 * When disabled, uses the primary background.
 *
 * @param config - The site configuration
 * @param sectionName - The name of the section (used for legacy compatibility)
 * @param sectionIndex - Optional: The index of the section in the rendered order for proper alternation
 * @param fallbackClass - The fallback class to use if no palette class is found
 */
export const getSectionBackgroundForSection = (
  config: SiteConfig,
  _sectionName: string,
  sectionIndex?: number,
  fallbackClass: string = "bg-white dark:bg-gray-900"
): string => {
  const alternateEnabled = config.alternateBackgrounds?.enabled;

  // IMPORTANT: If alternate backgrounds are enabled but no index is provided,
  // return empty string to avoid duplicate backgrounds
  if (alternateEnabled && sectionIndex === undefined) {
    return "";
  }

  // Use index-based alternation if index is provided and alternate backgrounds are enabled
  const backgroundType =
    alternateEnabled && sectionIndex !== undefined
      ? getBackgroundTypeByIndex(sectionIndex)
      : "primary";

  if (!alternateEnabled) {
    // Use primary background when alternate backgrounds are disabled
    return getPaletteClass(config.theme, "backgroundPrimary", fallbackClass);
  }

  // Use palette classes for proper theme integration
  switch (backgroundType) {
    case "soft":
      return getPaletteClass(
        config.theme,
        "backgroundSoft",
        "bg-gray-50 dark:bg-gray-900"
      );
    case "lighter":
      return getPaletteClass(
        config.theme,
        "backgroundLighter",
        "bg-white dark:bg-gray-900"
      );
    case "pattern":
      return getPaletteClass(
        config.theme,
        "backgroundPattern",
        "bg-gray-200 dark:bg-gray-800"
      );
    default:
      return getPaletteClass(config.theme, "backgroundPrimary", fallbackClass);
  }
};

/**
 * Checks if a section should use its own background or rely on alternate backgrounds.
 */
export const shouldUseSectionBackground = (config: SiteConfig): boolean => {
  return !config.alternateBackgrounds?.enabled;
};


/**
 * Gets the appropriate background class for a section with proper alternate backgrounds support.
 * This is the recommended function to use for section backgrounds.
 *
 * @param config - The site configuration
 * @param sectionName - The name of the section (e.g., 'hero', 'about', 'services')
 * @param sectionIndex - Optional: The index of the section in the rendered order for proper alternation
 * @param fallbackClass - The fallback class to use if no palette class is found
 * @returns The appropriate background class
 */
export const getSectionBackgroundClassForSection = (
  config: SiteConfig,
  sectionName: string,
  sectionIndex?: number,
  fallbackClass: string = "bg-white dark:bg-gray-900"
): string => {
  return getSectionBackgroundForSection(
    config,
    sectionName,
    sectionIndex,
    fallbackClass
  );
};

/**
 * Determines if a section should use a contrasting text color based on its background.
 * Useful for sections with darker alternate backgrounds.
 */
// Removed legacy shouldUseContrastingText; use sectionHasDarkBackground + getCardTextColorForSection instead

/**
 * Determines if a section has a darker background that requires lighter cards.
 * Returns true for sections with "soft" backgrounds (slate-800 in dark mode).
 */
export const sectionHasDarkBackground = (
  config: SiteConfig,
  _sectionName: string,
  sectionIndex?: number
): boolean => {
  const alternateEnabled = config.alternateBackgrounds?.enabled ?? false;
  if (!alternateEnabled || sectionIndex === undefined || sectionIndex < 0) {
    return false;
  }
  // Match SectionBackgroundRenderer: odd sections use backgroundSoft (colored)
  // Treat all odd sections as "dark" for card contrast purposes.
  return sectionIndex % 2 === 1;
};

/**
 * Gets the appropriate card background class based on the section's background.
 * Automatically provides contrasting backgrounds for better visual hierarchy.
 */
export const getCardBackgroundForSection = (
  config: SiteConfig,
  sectionName: string,
  cardType: "default" | "featured" | "elevated" = "default",
  sectionIndex?: number
): string => {
  // Return fallback classes if config is undefined
  if (!config || !config.theme) {
    switch (cardType) {
      case "featured":
        return "bg-blue-50 dark:bg-blue-900/20";
      case "elevated":
        return "bg-gray-50 dark:bg-gray-800";
      default:
        return "bg-white dark:bg-gray-900";
    }
  }

  // Featured: use a subtle brand-tinted background in light and neutral in dark via palette
  if (cardType === "featured") {
    return getPaletteClass(config.theme, "backgroundTertiary");
  }

  const alternateEnabled = config.alternateBackgrounds?.enabled;
  const isDarkSectionBg = sectionHasDarkBackground(
    config,
    sectionName,
    sectionIndex
  );

  // Clean implementation without debug logging

  if (!alternateEnabled) {
    // Standard card backgrounds when alternate backgrounds are disabled
    let result: string;
    switch (cardType) {
      case "elevated":
        result = getPaletteClass(config.theme, "backgroundElevated");
        break;
      default:
        result = getPaletteClass(
          config.theme,
          "backgroundSurface",
          "bg-white dark:bg-gray-900"
        );
    }
    return result;
  }

  // When alternate backgrounds are enabled, adjust card backgrounds based on section
  if (isDarkSectionBg) {
    // Section has dark background (slate-800 in dark mode, green-50 in light mode)
    // Use contrasting cards that work in both modes
    let result: string;
    switch (cardType) {
      case "elevated":
        result = getPaletteClass(config.theme, "backgroundElevated");
        break;
      default:
        result = getPaletteClass(
          config.theme,
          "backgroundCard",
          "bg-white dark:bg-gray-950"
        );
    }
    return result;
  } else {
    // Section has light background, use subtle card backgrounds
    let result: string;
    switch (cardType) {
      case "elevated":
        result = getPaletteClass(config.theme, "backgroundElevated");
        break;
      default:
        result = getPaletteClass(
          config.theme,
          "backgroundSurface",
          "bg-white dark:bg-gray-900"
        );
    }
    return result;
  }
};

/**
 * Gets the appropriate text color for cards based on the section's background.
 * Ensures proper contrast when cards are on darker section backgrounds.
 */
export const getCardTextColorForSection = (
  config: SiteConfig,
  sectionName: string,
  textType: "primary" | "secondary" | "muted" = "primary",
  sectionIndex?: number
): string => {
  // Return fallback classes if config is undefined
  if (!config || !config.theme) {
    switch (textType) {
      case "secondary":
        return "text-gray-600 dark:text-gray-300";
      case "muted":
        return "text-gray-500 dark:text-gray-400";
      default:
        return "text-gray-900 dark:text-white";
    }
  }
  const isDarkSectionBg = sectionHasDarkBackground(
    config,
    sectionName,
    sectionIndex
  );

  if (!isDarkSectionBg) {
    // Standard text colors for normal sections
    let result: string;
    switch (textType) {
      case "secondary":
        result = getPaletteClass(
          config.theme,
          "textSecondary",
          "text-gray-600 dark:text-gray-300"
        );
        break;
      case "muted":
        result = getPaletteClass(
          config.theme,
          "textMuted",
          "text-gray-400 dark:text-gray-500"
        );
        break;
      default:
        result = getPaletteClass(
          config.theme,
          "textPrimary",
          "text-gray-900 dark:text-white"
        );
    }
    return result;
  } else {
    // Enhanced contrast for cards on dark section backgrounds
    let result: string;
    switch (textType) {
      case "secondary":
        result = getPaletteClass(
          config.theme,
          "textSecondary",
          "text-slate-300 dark:text-slate-400"
        );
        break;
      case "muted":
        result = getPaletteClass(
          config.theme,
          "textMuted",
          "text-slate-400 dark:text-slate-500"
        );
        break;
      default:
        result = getPaletteClass(
          config.theme,
          "textPrimary",
          "text-white dark:text-slate-100"
        );
    }
    return result;
  }
};

// Convenience functions
export const getCardText = getCardTextColorForSection;

/**
 * Gets the appropriate background style for a section (legacy inline styles).
 * Only returns inline styles for legacy themes that don't support the palette system.
 *
 * @deprecated Use getSectionBackgroundClassForSection instead for better Tailwind integration
 */
// Removed legacy inline background style helper (getSectionBackgroundStyleLegacy)

/**
 * Gets the background class for a section by index (for backward compatibility).
 * This alternates between primary and soft backgrounds.
 */
// Removed legacy background-by-index helper (getSectionBackgroundByIndex)

/**
 * Determines if a section has an alternate background based on its position in the rendered order.
 * This utility replicates the same logic used in SectionsRenderer to calculate section indices.
 *
 * @param config - The site configuration including visibility and section order
 * @param sectionName - The name of the section to check
 * @param content - The content object containing section data (needed for hero background detection)
 * @returns Object with isAlternate boolean and section index
 */
/**
 * Determine which sections will actually render based on content and configuration.
 * This is more reliable than checking DOM since it can be called during render.
 */
// Removed legacy alternate section detection (getSectionAlternateInfo, isSectionAlternate)

/**
 * Gets the appropriate card background class based on the section's background.
 * Use this utility in card components to get contrasting backgrounds.
 *
 * @param config - The site configuration
 * @param sectionName - The name of the section the card is rendered in
 * @param cardType - The type of card background needed
 * @param content - Optional content object for accurate section detection
 * @returns The appropriate background class for the card
 */

/**
 * Gets the appropriate text color for cards based on the section's background.
 * Use this utility in card components to get proper text contrast.
 *
 * @param config - The site configuration
 * @param sectionName - The name of the section the card is rendered in
 * @param textType - The type of text (primary, secondary, muted)
 * @param content - Optional content object for accurate section detection
 * @returns The appropriate text color class for the card
 */
// Removed legacy alternate-based text color helper; use getCardTextColorForSection directly

/**
 * Gets the appropriate dark mode background class/styles based on the configured mode.
 * Only applies when dark mode is enabled.
 *
 * @param config - The site configuration
 * @param sectionName - The name of the section (e.g., 'hero', 'header', 'about')
 * @param sectionIndex - The index of the section in the rendered order
 * @returns Object with className and optional inline style for the background
 */
export const getDarkModeBackground = (
  config: SiteConfig,
  sectionName: string,
  sectionIndex: number
): { className: string; style?: CSSProperties } => {
  const mode = config.alternateBackgrounds?.darkModeBackgroundMode ?? "advanced";
  const isHeroOrHeader = sectionName === "hero" || sectionName === "header";

  switch (mode) {
    case "minimalist":
      // Single consistent dark background for all sections
      return { className: "bg-black" };

    case "advanced":
      if (isHeroOrHeader) {
        // Hero and header stay pure black
        return { className: "bg-black" };
      }
      // Other sections get the gradient sheen effect with edge highlight
      return {
        className: "bg-black border-t border-white/[0.08]",
        style: {
          backgroundImage:
            "linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 0%, rgba(0, 0, 0, 0) 20%)",
        },
      };

    case "zebra":
    default:
      // Alternating deep black and very subtly lighter
      if (sectionIndex % 2 === 0) {
        return { className: "", style: { backgroundColor: "#000000" } };
      }
      // #0e0e0e is ~5% lighter than pure black (#000000)
      return { className: "", style: { backgroundColor: "#0e0e0e" } };
  }
};

// Export the new functions with shorter names for convenience
export const getSectionBg = getSectionBackgroundClassForSection;
export const getCardTextForAlternate = getCardTextColorForSection;
